import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGroups } from '../hooks/useGroups';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { groupService } from '../services/groupService';
import { storageService } from '../services/storageService';
import { GroupForm, CreateGroupPost, JoinRequestsModal } from '../components/groups';
import { PostCard } from '../components/PostCard';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/Avatar';
import {
  Users,
  Settings,
  UserPlus,
  UserMinus,
  Globe,
  Lock,
  Calendar,
  ArrowLeft,
  MessageSquare,
  Image,
  UserCheck
} from 'lucide-react';
import { UpdateGroupRequest } from '../types';
import { Post, postService } from '../services/postService';
import { Group, ReactionType } from '../types'; // Import Group and ReactionType
import { AxiosResponse } from 'axios';
import { useToast } from '../hooks/useToast';
import { Header } from '../components/Header';

export const GroupPage: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const {
    currentGroup,
    members,
    isLoading: groupLoading,
    error: groupError,
    getGroup,
    joinGroup,
    leaveGroup,
    updateGroup,
    loadMembers,
    clearError
  } = useGroups();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [postsPage, setPostsPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [joinedGroups, setJoinedGroups] = useState<Group[]>([]);
  const [joinedGroupsLoading, setJoinedGroupsLoading] = useState(false);
  const [joinedGroupsError, setJoinedGroupsError] = useState<string | null>(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const groupIdNum = groupId ? parseInt(groupId, 10) : null;

  // Load group data
  useEffect(() => {
    if (groupIdNum) {
      console.log('Loading group data for ID:', groupIdNum);
      getGroup(groupIdNum);
      loadMembers(groupIdNum, 0, 20).catch(error => {
        console.error('Failed to load members:', error);
      });
      // Only load posts if user is a member
      // Will be checked again after group data is loaded
      loadPendingRequestsCount();
    }
  }, [groupIdNum]);

  // Load pending requests count for owner/moderator
  const loadPendingRequestsCount = async () => {
    if (!groupIdNum) return;
    try {
      const response = await groupService.getPendingJoinRequests(groupIdNum, 0, 1);
      setPendingRequestsCount(response.totalElements || 0);
    } catch (error) {
      console.error('Failed to load pending requests count:', error);
    }
  };

  // Fetch joined groups
  useEffect(() => {
    const fetchJoinedGroups = async () => {
      if (!user?.id) return;

      setJoinedGroupsLoading(true);
      setJoinedGroupsError(null);

      try {
        const response: AxiosResponse<{ data: { groups: Group[]; totalElements: number } }> = await apiClient.get(
          `/api/groups/owner/${user.id}?page=0&size=10`
        );
        const groups = Array.isArray(response.data.data.groups) ? response.data.data.groups : []; // Ensure groups is an array
        const totalElements = response.data.data.totalElements ?? 0; // Default to 0 if undefined

        setJoinedGroups(groups);
        console.log(`Total groups: ${totalElements}`); // Debugging log
      } catch (error: any) {
        setJoinedGroupsError(error.message || 'Failed to load joined groups');
      } finally {
        setJoinedGroupsLoading(false);
      }
    };

    fetchJoinedGroups();
  }, [user?.id]);

  const loadGroupPosts = async (page = 0, append = false) => {
    if (!groupIdNum) return;
    
    setPostsLoading(true);
    setPostsError(null);
    try {
      const response = await groupService.getGroupPosts(groupIdNum, page, 10);
      const newPosts = response.content || [];
      
      setPosts(prev => append ? [...prev, ...newPosts] : newPosts);
      setPostsPage(page);
      setHasMorePosts(page < (response.totalPages - 1));
      setTotalPosts(response.totalElements);
      
      console.log('📝 Loaded group posts:', {
        page,
        count: newPosts.length,
        total: response.totalElements,
        totalPages: response.totalPages,
        hasMore: page < (response.totalPages - 1)
      });
    } catch (error: any) {
      setPostsError(error.message || 'Failed to load group posts');
      console.error('Failed to load group posts:', error);
      toast.error('Tải bài viết thất bại', 'Vui lòng thử lại');
    } finally {
      setPostsLoading(false);
    }
  };

  const loadMorePosts = () => {
    if (!postsLoading && hasMorePosts) {
      loadGroupPosts(postsPage + 1, true);
    }
  };

  const handleJoinGroup = async () => {
    if (!groupIdNum) return;
    
    setIsSubmitting(true);
    try {
      const joinResponse = await joinGroup(groupIdNum);
      
      // Show success message
      const roleTranslations: Record<string, string> = {
        'member': 'thành viên',
        'moderator': 'điều hành viên',
        'admin': 'quản trị viên',
        'owner': 'chủ sở hữu'
      };
      const roleText = roleTranslations[joinResponse.role] || joinResponse.role;
      toast.success('Tham gia nhóm thành công', `Bạn đã tham gia "${joinResponse.groupName}" với vai trò ${roleText}`);
      
      // Refresh group data to update membership status
      await getGroup(groupIdNum);
      await loadMembers(groupIdNum, 0, 20);
    } catch (error) {
      console.error('Failed to join group:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!groupIdNum) return;
    
    const confirmed = await toast.confirm(
      'Rời khỏi nhóm?',
      'Bạn sẽ không còn là thành viên của nhóm này.',
      {
        confirmText: 'Rời nhóm',
        cancelText: 'Hủy',
        type: 'warning',
        icon: 'warning'
      }
    );

    if (confirmed) {
      setIsSubmitting(true);
      try {
        const leaveResponse = await leaveGroup(groupIdNum);
        toast.success('Rời nhóm thành công', `Bạn đã rời khỏi "${leaveResponse.groupName}"`);
        
        // Refresh group data
        await getGroup(groupIdNum);
        await loadMembers(groupIdNum, 0, 20);
      } catch (error) {
        console.error('Failed to leave group:', error);
        toast.error('Rời nhóm thất bại', 'Vui lòng thử lại.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEditGroup = async (updateData: UpdateGroupRequest) => {
    if (!groupIdNum) return;
    
    setIsSubmitting(true);
    try {
      await updateGroup(groupIdNum, updateData);
      setShowEditForm(false);
      // Refresh group data
      await getGroup(groupIdNum);
    } catch (error) {
      console.error('Failed to update group:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReaction = async (postId: number, reaction: ReactionType) => {
    try {
      await postService.reactToPost(postId, { type: reaction });
      // Optimistic update - WebSocket will handle the actual count
      console.log('Reacted to post:', postId, reaction);
    } catch (error) {
      console.error('Failed to react to post:', error);
      toast.error('Reaction thất bại');
    }
  };

  const handleDeletePost = async (postId: number) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    setTotalPosts(prev => prev - 1);
  };

  const handleCreatePost = async (content: string, mediaFiles?: File[]) => {
    if (!groupIdNum) return;
    
    try {
      // Upload media files to CDN first
      const mediaUrls = mediaFiles && mediaFiles.length > 0 
        ? await Promise.all(mediaFiles.map(f => storageService.uploadFile(f)))
        : [];
      
      console.log('📤 Creating group post with media:', {
        groupId: groupIdNum,
        contentLength: content.length,
        mediaCount: mediaUrls.length,
        mediaUrls
      });
      
      // Use groupService.createGroupPost with proper endpoint
      const post = await groupService.createGroupPost(groupIdNum, {
        content,
        mediaUrls
      });
      
      console.log('✅ Group post created successfully:', post);
      
      // Refresh posts
      await loadGroupPosts();
      toast.success('Đăng bài thành công', 'Bài viết đã được chia sẻ trong nhóm');
    } catch (error) {
      console.error('❌ Failed to create group post:', error);
      toast.error('Đăng bài thất bại', 'Vui lòng thử lại');
      throw error;
    }
  };

  // Calculate membership status (must be before conditional returns)
  const isMember = currentGroup ? (
    currentGroup.userMembershipStatus === 'joined' || 
    currentGroup.member === true || 
    (currentGroup.userRole && currentGroup.userRole !== null)
  ) : false;
  
  const isPending = currentGroup ? (
    currentGroup.userMembershipStatus === 'pending' || 
    currentGroup.hasPendingRequest === true
  ) : false;

  // Load posts when membership status changes (must be before conditional returns)
  useEffect(() => {
    if (groupIdNum && isMember && currentGroup) {
      loadGroupPosts();
    }
  }, [groupIdNum, isMember, currentGroup?.id]);

  if (groupLoading && !currentGroup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải nhóm...</p>
        </div>
      </div>
    );
  }

  if (groupError || !currentGroup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <h2 className="text-xl font-semibold">Không tìm thấy nhóm</h2>
            <p className="text-gray-600 mt-2">
              {groupError || 'Nhóm bạn đang tìm kiếm không tồn tại hoặc bạn không có quyền truy cập.'}
            </p>
          </div>
          <div className="space-x-3">
            <Button variant="outline" onClick={() => navigate('/groups')}>
              Quay lại Nhóm
            </Button>
            {groupError && (
              <Button variant="primary" onClick={() => window.location.reload()}>
                Thử lại
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  const canManage = currentGroup.userRole === 'owner' || currentGroup.userRole === 'moderator' || currentGroup.userRole === 'admin';
  const isOwner = currentGroup.userRole === 'owner' || currentGroup.isOwner;
  const canPost = isMember;

  // Debug logging
  console.log('GroupPage render - members:', members, 'members type:', typeof members, 'members length:', members?.length);
  console.log('GroupPage membership status:', {
    userMembershipStatus: currentGroup.userMembershipStatus,
    member: currentGroup.member,
    userRole: currentGroup.userRole,
    isMember,
    canPost
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header showSearch={true} showUserMenu={true} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/groups')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Quay lại Nhóm
          </Button>
        </div>

        {/* Group Header */}
        <Card className="mb-8">
          {/* Cover Image */}
          {currentGroup.coverUrl && (
            <div className="h-48 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-t-lg relative overflow-hidden">
              <img
                src={currentGroup.coverUrl}
                alt={`${currentGroup.name} cover`}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                {/* Group Avatar */}
                <Avatar
                  user={{
                    avatarUrl: currentGroup.avatarUrl,
                    displayName: currentGroup.name,
                    userId: currentGroup.id
                  }}
                  size="xl"
                  className="ring-4 ring-white shadow-lg"
                />
                
                {/* Group Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {currentGroup.name}
                    </h1>
                    
                    {/* Privacy Badge */}
                    <div className="flex items-center space-x-1 text-gray-500">
                      {currentGroup.privacy === 'private_' ? (
                        <>
                          <Lock className="w-5 h-5" />
                          <span className="text-sm">Riêng tư</span>
                        </>
                      ) : (
                        <>
                          <Globe className="w-5 h-5" />
                          <span className="text-sm">Công khai</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  {currentGroup.description && (
                    <p className="text-gray-600 mb-4 max-w-2xl">
                      {currentGroup.description}
                    </p>
                  )}
                  
                  {/* Stats */}
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{currentGroup.memberCount} thành viên</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Tạo lúc {new Date(currentGroup.createdAt).toLocaleDateString('vi-VN')}</span>
                    </div>
                    
                    {currentGroup.requiresApproval && (
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs">
                        Yêu cầu phê duyệt
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {user && !isMember && !isPending && (
                  <Button
                    variant="primary"
                    onClick={handleJoinGroup}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    className="flex items-center"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {currentGroup.requiresApproval ? 'Yêu cầu tham gia' : 'Tham gia nhóm'}
                  </Button>
                )}
                
                {isPending && (
                  <Button variant="outline" disabled className="flex items-center">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Yêu cầu đang chờ
                  </Button>
                )}
                
                {isMember && !isOwner && (
                  <Button
                    variant="outline"
                    onClick={handleLeaveGroup}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    className="flex items-center text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    Rời khỏi nhóm
                  </Button>
                )}
                
                {canManage && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setShowJoinRequestsModal(true)}
                      className="flex items-center relative"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Yêu cầu tham gia
                      {pendingRequestsCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {pendingRequestsCount}
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowEditForm(true)}
                      className="flex items-center"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Cài đặt
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Create Post */}
            <div data-create-post>
              <CreateGroupPost
                groupId={groupIdNum!}
                groupName={currentGroup.name}
                onCreatePost={handleCreatePost}
                isLoading={isSubmitting}
                disabled={!canPost}
              />
            </div>

            {/* Posts */}
            <div className="space-y-6">
              {!isMember ? (
                <Card className="p-8 text-center">
                  <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {currentGroup.privacy === 'private_' ? 'Nhóm riêng tư' : 'Bạn chưa tham gia nhóm'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Bạn cần tham gia nhóm để xem bài viết và tương tác với các thành viên.
                  </p>
                  {isPending ? (
                    <div className="inline-flex items-center px-4 py-2 border border-yellow-300 rounded-md text-sm font-medium text-yellow-700 bg-yellow-50">
                      <UserCheck className="w-4 h-4 mr-2" />
                      Yêu cầu đang chờ phê duyệt
                    </div>
                  ) : (
                    <Button 
                      variant="primary" 
                      onClick={handleJoinGroup}
                      disabled={isSubmitting}
                      className="inline-flex items-center"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {isSubmitting ? 'Đang gửi yêu cầu...' : 'Tham gia nhóm'}
                    </Button>
                  )}
                </Card>
              ) : (
                <>
                  {postsLoading && posts.length === 0 && (
                    <Card className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Đang tải bài viết...</p>
                    </Card>
                  )}
                  
                  {postsError && posts.length === 0 && (
                    <Card className="p-8 text-center">
                      <div className="text-red-500 mb-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Không thể tải bài viết</h3>
                        <p className="text-gray-600">{postsError}</p>
                      </div>
                      <Button variant="outline" onClick={() => loadGroupPosts()}>
                        Thử lại
                      </Button>
                    </Card>
                  )}
                  
                  {!postsLoading && !postsError && posts.length === 0 && (
                    <Card className="p-8 text-center">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có bài viết</h3>
                      <p className="text-gray-600 mb-4">
                        {canPost 
                          ? "Hãy là người đầu tiên chia sẻ điều gì đó với nhóm này!"
                          : "Chưa có bài viết nào được chia sẻ trong nhóm này."
                        }
                      </p>
                      {canPost && (
                        <Button variant="primary" onClick={() => {
                          // Scroll to the create post component
                          const createPostElement = document.querySelector('[data-create-post]');
                          if (createPostElement) {
                            createPostElement.scrollIntoView({ behavior: 'smooth' });
                            // Trigger the textarea click
                            const textarea = createPostElement.querySelector('textarea');
                            if (textarea) {
                              textarea.focus();
                            }
                          }
                        }}>
                          Tạo bài viết đầu tiên
                        </Button>
                      )}
                    </Card>
                  )}
                </>
              )}
              
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onReaction={handleReaction}
                  onUnreact={async (postId) => {
                    try {
                      await postService.unreactPost(postId);
                    } catch (error) {
                      console.error('Failed to unreact:', error);
                    }
                  }}
                  onComment={async () => {}}
                  onShare={async () => {}}
                  onDelete={handleDeletePost}
                />
              ))}
              
              {/* Load More Button */}
              {hasMorePosts && posts.length > 0 && (
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={loadMorePosts}
                    disabled={postsLoading}
                    variant="outline"
                    className="min-w-[200px]"
                  >
                    {postsLoading ? 'Tải thêm...' : 'Xem thêm bài viết'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Members */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Thành viên</h3>
                  <span className="text-sm text-gray-500">{currentGroup.memberCount}</span>
                </div>
                
                <div className="space-y-3">
                  {members && Array.isArray(members) && members.length > 0 ? members.slice(0, 8).map((member) => (
                    <div key={member.id || member.userId} className="flex items-center space-x-3">
                      <Avatar
                        user={{
                          avatarUrl: member.avatarUrl || member.user?.avatarUrl,
                          displayName: member.displayName || member.user?.displayName,
                          userId: member.userId || member.user?.id
                        }}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {member.displayName || member.user?.displayName}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {member.role}
                        </p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500">Chưa có thành viên</p>
                  )}
                  
                  {members && Array.isArray(members) && members.length > 8 && (
                    <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                      Xem tất cả thành viên
                    </button>
                  )}
                </div>
              </div>
            </Card>

            {/* Group Info */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Giới thiệu</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">Quyền riêng tư:</span>
                    <span className="text-gray-900 capitalize">
                      {currentGroup.privacy === 'private_' ? 'Riêng tư' : 'Công khai'}
                    </span>
                  </div>
                  
                  {(currentGroup.ownerDisplayName || currentGroup.owner) && (
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Tạo bởi:</span>
                      <span className="text-gray-900">
                        {currentGroup.ownerDisplayName || currentGroup.owner?.displayName}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">Tạo lúc:</span>
                    <span className="text-gray-900">
                      {new Date(currentGroup.createdAt).toLocaleDateString('vi-VN')}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Joined Groups Section */}
            <div className="space-y-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Nhóm bạn đã tham gia</h3>

                  {joinedGroupsLoading && (
                    <div className="text-center text-gray-500">Đang tải...</div>
                  )}

                  {joinedGroupsError && (
                    <div className="text-center text-red-500">{joinedGroupsError}</div>
                  )}

                  {!joinedGroupsLoading && (!joinedGroups || joinedGroups.length === 0) && (
                    <div className="text-center text-gray-500">Bạn chưa tham gia nhóm nào.</div>
                  )}

                  {joinedGroups && joinedGroups.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {joinedGroups.map((group) => (
                        <Card key={group.id} className="p-4">
                          <div className="flex items-center space-x-4">
                            <Avatar
                              user={{
                                avatarUrl: group.avatarUrl,
                                displayName: group.name,
                                userId: group.id,
                              }}
                              size="sm"
                            />
                            <div>
                              <h4 className="text-md font-medium text-gray-900">{group.name}</h4>
                              <p className="text-sm text-gray-500">{group.description}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Join Requests Modal */}
        <JoinRequestsModal
          isOpen={showJoinRequestsModal}
          onClose={() => {
            setShowJoinRequestsModal(false);
            loadPendingRequestsCount();
          }}
          groupId={currentGroup.id}
          groupName={currentGroup.name}
        />

        {/* Edit Group Form */}
        {showEditForm && (
          <GroupForm
            isOpen={showEditForm}
            onClose={() => setShowEditForm(false)}
            onSubmit={async (data) => {
              // Ensure we only pass UpdateGroupRequest data, not FormData
              if (!(data instanceof FormData)) {
                await handleEditGroup({
                  name: data.name,
                  description: data.description,
                  privacy: data.privacy,
                  requiresApproval: data.requiresApproval,
                  avatarUrl: data.avatarUrl,
                  coverUrl: data.coverUrl
                });
              }
            }}
            initialData={{
              name: currentGroup.name,
              description: currentGroup.description || '',
              privacy: currentGroup.privacy,
              avatarUrl: currentGroup.avatarUrl,
              coverUrl: currentGroup.coverUrl,
              requiresApproval: currentGroup.requiresApproval
            }}
            isEditing={true}
            isLoading={isSubmitting}
            title="Chỉnh sửa nhóm"
          />
        )}

      </div>
    </div>
  );
};